/**
 * AdminLayout — persistent shell for all admin pages.
 *
 * Desktop: collapsible left sidebar (w-56 expanded / w-14 collapsed).
 * Mobile:  sticky header + bottom nav (4 primary items + More drawer).
 *
 * Nav visibility is permission-gated:
 *   Applications  → all staff
 *   Applicants    → admin | users_access
 *   Messages      → admin | messages_read
 *   Team          → admin only
 *   Leads         → admin | leads_access
 *   Broadcast     → admin | broadcast
 *   AI Monitor    → admin only
 *   Activity      → admin only
 *   Settings      → admin only
 */

import { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useStaffRole } from "@/hooks/useStaffRole";
import { supabase } from "@/integrations/supabase/client";
import { useUnreadThreadCount } from "@/hooks/useAdminThreads";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import SessionGuard from "@/components/SessionGuard";
import AppErrorBoundary from "@/components/AppErrorBoundary";
import {
  FileText, Users, MessageSquare, Shield,
  ClipboardList, Radio, Activity, Settings, Mail, TrendingUp,
  ChevronLeft, ChevronRight, Menu, LogOut, X, LayoutDashboard, CreditCard,
  Package, Headset, KanbanSquare, Bot, Link2, Handshake, KeyRound, Phone } from "lucide-react";
import logo from "@/assets/logo-thailand-elite.png";

// ─── Types ────────────────────────────────────────────────────────────────────

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  badge?: number;
}

// ─── AdminLayout ─────────────────────────────────────────────────────────────

const AdminLayout = () => {
  const { user, signOut } = useAuth();
  const { isAdmin, role, permissions, loading: roleLoading } = useStaffRole();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: unreadCount } = useUnreadThreadCount();

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [profile, setProfile] = useState<{ given_name: string | null; surname: string | null; email: string | null } | null>(null);

  // Fetch current user's profile for display
  useEffect(() => {
    if (!user?.id) return;
    supabase.from("profiles").select("given_name, surname, email").eq("id", user.id).single()
      .then(({ data }) => { if (data) setProfile(data); });
  }, [user?.id]);

  const displayName = profile
    ? [profile.given_name, profile.surname].filter(Boolean).join(" ") || profile.email || user?.email || "Admin"
    : user?.email || "Admin";
  const displayRole = isAdmin ? "Administrator" : role === "agent" ? "Agent" : role ? role : "Staff";

  // Close drawer on route change
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // ── Permission-gated nav items ─────────────────────────────────────────────

  // Nav is grouped into sections. Each item keeps its own permission gate
  // (`allowed`); a whole section is hidden when the staff member has access to
  // none of its items — so a sales/support agent sees the Main section, gains
  // the Members section only if granted member access, and admins see all.
  // `primary` marks items surfaced in the mobile bottom bar.
  type NavSection = { title: string | null; items: Array<NavItem & { primary?: boolean; allowed: boolean }> };
  const SECTIONS: NavSection[] = [
    {
      title: null, // Main / sales workspace
      items: [
        { primary: true,  allowed: isAdmin || (permissions?.applications ?? "none") !== "none", label: "Overview",     path: "/admin/overview",     icon: <LayoutDashboard className="h-4 w-4 shrink-0" /> },
        { primary: true,  allowed: isAdmin || (permissions?.applications ?? "none") !== "none", label: "Pipeline",     path: "/admin/pipeline",     icon: <KanbanSquare className="h-4 w-4 shrink-0" /> },
        { primary: false, allowed: isAdmin || !!permissions?.leads_access,                      label: "Contact follow-up",        path: "/admin/leads",        icon: <ClipboardList className="h-4 w-4 shrink-0" /> },
        { primary: true,  allowed: isAdmin || !!permissions?.users_access,                      label: "Users",        path: "/admin/applicants",   icon: <Users className="h-4 w-4 shrink-0" /> },
        { primary: true,  allowed: true,                                                        label: "Applications", path: "/admin/applications", icon: <FileText className="h-4 w-4 shrink-0" /> },
        { primary: true,  allowed: isAdmin || !!permissions?.messages_read,                     label: "Messages",     path: "/admin/messages",     icon: <MessageSquare className="h-4 w-4 shrink-0" />, badge: unreadCount > 0 ? unreadCount : undefined },
      ],
    },
    {
      title: "Members",
      items: [
        { allowed: isAdmin || (permissions?.members_access ?? "none") !== "none",          label: "Members",          path: "/admin/members",          icon: <CreditCard className="h-4 w-4 shrink-0" /> },
        { allowed: isAdmin || (permissions?.service_requests_access ?? "none") !== "none", label: "Service Requests", path: "/admin/service-requests", icon: <Headset className="h-4 w-4 shrink-0" /> },
        { allowed: isAdmin || !!permissions?.member_link_approval,                         label: "Member Links",     path: "/admin/member-links",     icon: <Link2 className="h-4 w-4 shrink-0" /> },
        { allowed: isAdmin || (permissions?.partners_access ?? "none") !== "none",         label: "Partners",         path: "/admin/partners",         icon: <Handshake className="h-4 w-4 shrink-0" /> },
      ],
    },
    {
      title: "Administration",
      items: [
        { allowed: isAdmin || !!permissions?.analytics_access, label: "Analytics",  path: "/admin/analytics",     icon: <TrendingUp className="h-4 w-4 shrink-0" /> },
        { allowed: isAdmin || !!permissions?.broadcast,        label: "Broadcast",  path: "/admin/broadcast",     icon: <Radio className="h-4 w-4 shrink-0" /> },
        { allowed: isAdmin,                                    label: "Team",       path: "/admin/team",          icon: <Shield className="h-4 w-4 shrink-0" /> },
        { allowed: isAdmin,                                    label: "Activity",   path: "/admin/activity",      icon: <Activity className="h-4 w-4 shrink-0" /> },
        { allowed: isAdmin,                                    label: "AI Monitor", path: "/admin/ai-monitoring", icon: <Bot className="h-4 w-4 shrink-0" /> },
        { allowed: isAdmin,                                    label: "Calls",      path: "/admin/calls",         icon: <Phone className="h-4 w-4 shrink-0" /> },
        { allowed: isAdmin,                                    label: "Email",      path: "/admin/email",         icon: <Mail className="h-4 w-4 shrink-0" /> },
        { allowed: isAdmin,                                    label: "Packages",   path: "/admin/packages",      icon: <Package className="h-4 w-4 shrink-0" /> },
        { allowed: isAdmin,                                    label: "API Keys",   path: "/admin/api-keys",      icon: <KeyRound className="h-4 w-4 shrink-0" /> },
        { allowed: isAdmin,                                    label: "Settings",   path: "/admin/settings",      icon: <Settings className="h-4 w-4 shrink-0" /> },
      ],
    },
  ];

  const allItems = SECTIONS.flatMap((s) => s.items);

  // Only show items the user has access to. Sections with no allowed items are
  // dropped entirely (so the section header doesn't show for staff without scope).
  const primaryItems = allItems.filter((i) => i.primary && i.allowed);
  const visibleSections = SECTIONS
    .map((s) => ({ title: s.title, items: s.items.filter((i) => i.allowed) }))
    .filter((s) => s.items.length > 0);

  // Build a set of allowed paths for route-guard
  const allowedPaths = new Set(allItems.filter((i) => i.allowed).map((i) => i.path));

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  // ── Route guard: redirect if the user hits a path they can't access ────────
  // Skip while role is still loading to avoid flash redirects
  if (!roleLoading) {
    const currentProtected = allItems.find((i) => location.pathname.startsWith(i.path));
    if (currentProtected && !currentProtected.allowed) {
      return <Navigate to="/admin/applications" replace />;
    }
  }

  // ── Shared NavLink ─────────────────────────────────────────────────────────

  const NavLink = ({ item, collapsed = false }: { item: NavItem; collapsed?: boolean }) => (
    <button
      onClick={() => navigate(item.path)}
      title={collapsed ? item.label : undefined}
      className={`
        relative flex items-center gap-3 w-full rounded-lg px-3 py-2 text-sm font-medium transition-colors
        ${isActive(item.path)
          ? "bg-primary text-primary-foreground"
          : "text-muted-foreground hover:bg-secondary hover:text-foreground"}
        ${collapsed ? "justify-center px-2" : ""}
      `}
    >
      {item.icon}
      {!collapsed && <span className="truncate">{item.label}</span>}
      {item.badge !== undefined && (
        <span className={`
          flex items-center justify-center rounded-full text-[10px] font-bold leading-none bg-red-500 text-white
          ${collapsed
            ? "absolute -top-1 -right-1 h-4 w-4"
            : "ml-auto h-5 min-w-5 px-1"}
        `}>
          {item.badge > 9 ? "9+" : item.badge}
        </span>
      )}
    </button>
  );

  // ── Desktop sidebar ────────────────────────────────────────────────────────

  const Sidebar = () => (
    <aside className={`
      hidden md:flex flex-col border-r border-border bg-card h-screen sticky top-0 shrink-0
      transition-all duration-200
      ${sidebarCollapsed ? "w-14" : "w-56"}
    `}>
      {/* Logo */}
      <div className={`flex items-center gap-2 px-3 py-4 border-b border-border ${sidebarCollapsed ? "justify-center" : ""}`}>
        <img src={logo} alt="Thailand Elite" className="h-8 shrink-0" />
        {!sidebarCollapsed && (
          <div className="min-w-0">
            <p className="text-xs font-bold leading-tight truncate" title={displayName}>{displayName}</p>
            <p className="text-[10px] text-muted-foreground truncate capitalize">{displayRole}</p>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-1">
        {visibleSections.map((section, si) => (
          <div key={si} className={si > 0 ? "pt-2" : ""}>
            {si > 0 && !sidebarCollapsed && section.title && (
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground px-3 pb-1">{section.title}</p>
            )}
            {si > 0 && sidebarCollapsed && <div className="mb-2 border-t border-border" />}
            <div className="space-y-1">
              {section.items.map((item) => (
                <NavLink key={item.path} item={item} collapsed={sidebarCollapsed} />
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="border-t border-border px-2 py-3 space-y-1">
        <button
          onClick={() => signOut()}
          title={sidebarCollapsed ? "Sign Out" : undefined}
          className={`flex items-center gap-3 w-full rounded-lg px-3 py-2 text-sm text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors ${sidebarCollapsed ? "justify-center px-2" : ""}`}
        >
          <LogOut className="h-4 w-4 shrink-0" />
          {!sidebarCollapsed && <span>Sign Out</span>}
        </button>
        <button
          onClick={() => setSidebarCollapsed((v) => !v)}
          title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          className="flex items-center w-full rounded-lg px-3 py-2 text-xs text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors justify-center gap-2"
        >
          {sidebarCollapsed
            ? <ChevronRight className="h-4 w-4" />
            : <><ChevronLeft className="h-4 w-4" /><span className="ml-auto">Collapse</span></>}
        </button>
      </div>
    </aside>
  );

  // ── Mobile header ──────────────────────────────────────────────────────────

  const MobileHeader = () => (
    <header className="md:hidden sticky top-0 z-40 bg-card border-b border-border px-4 py-3 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <img src={logo} alt="Thailand Elite" className="h-8" />
        <div className="min-w-0">
          <p className="text-sm font-bold leading-tight truncate max-w-[140px]">{displayName}</p>
          <p className="text-[10px] text-muted-foreground truncate capitalize">{displayRole}</p>
        </div>
      </div>
      <div className="flex items-center gap-1">
        <Button variant="ghost" size="sm" onClick={() => signOut()} className="h-8 w-8 p-0">
          <LogOut className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setDrawerOpen(true)} className="h-8 w-8 p-0">
          <Menu className="h-4 w-4" />
        </Button>
      </div>
    </header>
  );

  // ── Mobile bottom nav: 4 primary + More ───────────────────────────────────

  const BottomNav = () => (
    <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-card border-t border-border flex items-stretch">
      {primaryItems.slice(0, 4).map((item) => (
        <button
          key={item.path}
          onClick={() => navigate(item.path)}
          className={`
            relative flex-1 flex flex-col items-center gap-0.5 py-2 px-1 text-[10px] font-medium transition-colors
            ${isActive(item.path) ? "text-primary" : "text-muted-foreground"}
          `}
        >
          <div className="relative">
            {item.icon}
            {item.badge !== undefined && (
              <span className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center leading-none">
                {item.badge > 9 ? "9+" : item.badge}
              </span>
            )}
          </div>
          <span className="leading-tight truncate max-w-full">{item.label}</span>
        </button>
      ))}
      <button
        onClick={() => setDrawerOpen(true)}
        className="flex-1 flex flex-col items-center gap-0.5 py-2 px-1 text-[10px] font-medium text-muted-foreground"
      >
        <Menu className="h-4 w-4 shrink-0" />
        <span className="leading-tight">More</span>
      </button>
    </nav>
  );

  // ── Mobile drawer ──────────────────────────────────────────────────────────

  const MobileDrawer = () => (
    <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
      <SheetContent side="right" className="w-72 p-0">
        <SheetHeader className="px-4 py-4 border-b border-border">
          <div className="flex items-center justify-between">
            <SheetTitle className="flex items-center gap-2">
              <img src={logo} alt="Thailand Elite" className="h-7" />
              <div className="min-w-0">
                <p className="text-sm font-bold leading-tight truncate">{displayName}</p>
                <p className="text-[10px] text-muted-foreground font-normal truncate capitalize">{displayRole}</p>
              </div>
            </SheetTitle>
            <button onClick={() => setDrawerOpen(false)} className="text-muted-foreground hover:text-foreground">
              <X className="h-4 w-4" />
            </button>
          </div>
        </SheetHeader>

        <nav className="px-3 py-4 space-y-1">
          {visibleSections.map((section, si) => (
            <div key={si} className={si > 0 ? "pt-3" : ""}>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground px-3 mb-2">{section.title ?? "Navigation"}</p>
              {section.items.map((item) => (
                <NavLink key={item.path} item={item} />
              ))}
            </div>
          ))}

          <div className="my-3 border-t border-border" />
          <button
            onClick={() => { signOut(); setDrawerOpen(false); }}
            className="flex items-center gap-3 w-full rounded-lg px-3 py-2 text-sm text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
          >
            <LogOut className="h-4 w-4 shrink-0" />
            <span>Sign Out</span>
          </button>
        </nav>
      </SheetContent>
    </Sheet>
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="flex h-screen bg-secondary/30">
      <SessionGuard inactivityMs={45 * 60 * 1000} />
      <Sidebar />

      <div className="flex-1 flex flex-col min-w-0 overflow-y-auto">
        <MobileHeader />
        <main className="flex-1 px-4 sm:px-6 py-6 pb-24 md:pb-6">
          <AppErrorBoundary context="admin" resetKey={location.pathname + location.search}>
            <Outlet />
          </AppErrorBoundary>
        </main>
      </div>

      <BottomNav />
      <MobileDrawer />
    </div>
  );
};

export default AdminLayout;
